import { z } from 'zod';
import { CalendarDate } from './project.js';
/**
 * Objective & Key Result — APM-705, spec mục 11.
 *
 * ## Vì sao tiến độ mục tiêu tách khỏi tiến độ task
 *
 * Spec nói thẳng: "không lẫn với tiến độ task thuần tuý". Làm xong 100% task mà
 * chỉ số vẫn không nhúc nhích là một kết quả **có thật**, và nó là thứ ban lãnh
 * đạo cần nhìn thấy nhất. Gộp hai thanh tiến độ vào một là giấu đúng cái đó.
 */
export const ObjectiveStatus = z.enum(['active', 'achieved', 'missed', 'archived']);
export const KeyResultSource = z.enum(['manual', 'integration']);
/**
 * Tiến độ của một key result, tính trên đoạn đường `start → target`.
 *
 * ⚠️ Không phải `current / target`. Mục tiêu hay ở dạng **giảm** ("giảm thời
 * gian xử lý xuống dưới 2s"): với công thức kia thì lúc còn tệ nhất (5s) tiến
 * độ là 250%, và càng tốt lên con số càng nhỏ. Đo trên đoạn đường thì cả hai
 * chiều đều đúng, và `start === target` — một mục tiêu không có gì để đi — trả
 * về 100 chứ không chia cho 0.
 *
 * `null` = **chưa báo số lần nào** (APM-OBJ-a): mục tiêu SMART chỉ khai giá trị
 * cần đạt, và lần báo số đầu tiên mới là mốc xuất phát. Trả 0% ở đây là nói
 * "đã đo, và đang ở vạch xuất phát" — một câu khác hẳn "chưa đo".
 */
export function keyResultProgress(start, target, current, options = {}) {
    if (start === null || current === null)
        return null;
    if (start === target)
        return current === target ? 100 : 0;
    const ratio = (current - start) / (target - start);
    /*
      Sàn **luôn** kẹp ở 0: đi lùi so với mốc xuất phát là 0%, không phải một số
      âm. Một thanh tiến độ âm không vẽ được, và "-40%" đọc như một phép đo khác.
  
      Trần thì tuỳ người gọi. Chặn trên ở 999 ở cả hai nhánh: một mốc xuất phát
      sát đích (`target - start` gần 0) cho ra những con số vô nghĩa kiểu 40 000%,
      và chúng phá vỡ bố cục trước khi nói được điều gì.
    */
    const floored = Math.max(ratio, 0);
    const capped = options.cap === false ? Math.min(floored, 9.99) : Math.min(floored, 1);
    return Math.round(capped * 100);
}
// ── Input ───────────────────────────────────────────────────────────────────
const Measure = z.number().finite();
export const CreateObjectiveInput = z.object({
    title: z.string().trim().min(1).max(300),
    description: z.string().max(5_000).optional(),
    ownerId: z.uuid().optional(),
    /**
     * Giá trị cần đạt — mục tiêu SMART (APM-OBJ-a).
     *
     * Có trường này thì mục tiêu được tạo **kèm đúng một chỉ số** trong cùng một
     * lệnh: tên chỉ số là tên mục tiêu, chưa có mốc xuất phát (lần báo số đầu sẽ
     * đặt). Hai lời gọi riêng thì lời thứ hai hỏng là để lại một mục tiêu không
     * đo được mà không ai biết.
     */
    target: z.object({ value: Measure, unit: z.string().max(30).trim().optional() }).optional(),
    /**
     * Mục tiêu của **một sprint** thay vì cả dự án — APM-OBJ-b. Sprint phải thuộc
     * đúng dự án này; bỏ trống = mục tiêu của dự án.
     */
    sprintId: z.uuid().optional(),
});
export const UpdateObjectiveInput = z.object({
    title: z.string().trim().min(1).max(300).optional(),
    description: z.string().max(5_000).nullable().optional(),
    ownerId: z.uuid().nullable().optional(),
    status: ObjectiveStatus.optional(),
});
export const CreateKeyResultInput = z.object({
    title: z.string().trim().min(1).max(300),
    unit: z.string().max(30).optional(),
    /** Mốc xuất phát. Bỏ trống ⇒ lấy bằng `currentValue`. */
    startValue: Measure.optional(),
    targetValue: Measure,
    currentValue: Measure.optional(),
    source: KeyResultSource.optional(),
    dueDate: CalendarDate.optional(),
});
export const UpdateKeyResultInput = z.object({
    title: z.string().trim().min(1).max(300).optional(),
    unit: z.string().max(30).nullable().optional(),
    startValue: Measure.optional(),
    targetValue: Measure.optional(),
    source: KeyResultSource.optional(),
    dueDate: CalendarDate.nullable().optional(),
});
/**
 * Cập nhật giá trị hiện tại — thao tác **riêng**, không nằm trong `update`.
 *
 * Nó là thứ xảy ra hằng tuần và là thứ duy nhất tạo ra lịch sử đáng đọc, nên nó
 * có event riêng (`key_result.value_updated`) và có chỗ ghi lý do. Nhét chung
 * vào `PATCH` thì mỗi lần sửa tiêu đề cũng thành một điểm trên biểu đồ.
 */
export const CheckInKeyResultInput = z.object({
    value: Measure,
    /** "Vì sao con số nhảy" — thứ người đọc biểu đồ ba tháng sau đi tìm. */
    note: z.string().max(500).optional(),
});
export const LinkObjectiveTasksInput = z.object({
    taskIds: z.array(z.uuid()).min(1).max(100),
});
// ── View ────────────────────────────────────────────────────────────────────
export const KeyResultView = z.object({
    id: z.uuid(),
    objectiveId: z.uuid(),
    title: z.string(),
    unit: z.string().nullable(),
    /** `null` = chưa báo số lần nào — lần báo đầu tiên là mốc xuất phát. */
    startValue: z.number().nullable(),
    targetValue: z.number(),
    currentValue: z.number().nullable(),
    /** `keyResultProgress` tính ở server để hai đầu không ra hai con số. */
    /** `null` = chưa đo được: chưa có lần báo số nào. */
    progressPct: z.number().int().min(0).max(100).nullable(),
    source: KeyResultSource,
    dueDate: CalendarDate.nullable(),
    /** Quá hạn mà chưa đạt 100%. */
    overdue: z.boolean(),
    updatedAt: z.iso.datetime(),
});
export const ObjectiveTaskRef = z.object({
    taskId: z.uuid(),
    taskKey: z.string(),
    title: z.string(),
    statusCategory: z.enum(['todo', 'in_progress', 'done']),
});
export const ObjectiveView = z.object({
    id: z.uuid(),
    projectId: z.uuid(),
    /** `null` = mục tiêu của cả dự án; có giá trị = mục tiêu của sprint ấy (APM-OBJ-b). */
    sprintId: z.uuid().nullable(),
    title: z.string(),
    description: z.string().nullable(),
    status: ObjectiveStatus,
    ownerId: z.uuid().nullable(),
    ownerName: z.string().nullable(),
    position: z.number().int().nonnegative(),
    /**
     * Trung bình cộng tiến độ các key result. `null` khi chưa có KR nào — một
     * mục tiêu không có chỉ số thì **không đo được**, và hiện 0% ở đó là nói dối
     * theo hướng bi quan.
     */
    progressPct: z.number().int().min(0).max(100).nullable(),
    keyResults: z.array(KeyResultView),
    /**
     * Task phục vụ mục tiêu này — traceability của spec mục 11. Rỗng nghĩa là
     * mục tiêu **không có việc nào phục vụ**, và đó là phát hiện, không phải chỗ trống.
     */
    tasks: z.array(ObjectiveTaskRef),
    /** Phần task đã xong trong số task phục vụ mục tiêu. */
    taskDonePct: z.number().int().min(0).max(100).nullable(),
    createdAt: z.iso.datetime(),
    updatedAt: z.iso.datetime(),
});
/**
 * Một lần cập nhật giá trị, đọc từ `event_log`.
 *
 * Không có bảng lịch sử riêng: event log **là** lịch sử (ADR-001). Đây cũng là
 * lý do "snapshot objective theo từng version" (spec mục 11) không cần bảng nào
 * — trạng thái tại `v2` là replay tới đúng `project_version.event_log_id`.
 */
export const KeyResultCheckIn = z.object({
    value: z.number(),
    previousValue: z.number().nullable(),
    note: z.string().nullable(),
    at: z.iso.datetime(),
    by: z.uuid().nullable(),
    byName: z.string().nullable(),
});
//# sourceMappingURL=objective.js.map